import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { productAPI } from '../../api'
import type { CatalogProductFormData } from '../../types'
import { optionKeys } from './useOptions'
import { categoryKeys } from './useCategories'

// Query key factory for products
export const productKeys = {
  all: ['products'] as const,
  lists: () => [...productKeys.all, 'list'] as const,
  list: (filters: ProductFilters) => [...productKeys.lists(), filters] as const,
  details: () => [...productKeys.all, 'detail'] as const,
  detail: (id: number) => [...productKeys.details(), id] as const,
  related: (id: number) => [...productKeys.all, 'related', id] as const,
}

export interface ProductFilters {
  page?: number
  perPage?: number
  search?: string
  category?: string
  productType?: string
  manufacturer?: string
  includeInactive?: boolean
  inactiveOnly?: boolean
}

// Fetch products list with filters
export function useProducts(filters: ProductFilters = {}) {
  const {
    page = 1,
    perPage = 20,
    search = '',
    category = '',
    productType = '',
    manufacturer = '',
    includeInactive = false,
    inactiveOnly = false,
  } = filters

  return useQuery({
    queryKey: productKeys.list({ page, perPage, search, category, productType, manufacturer, includeInactive, inactiveOnly }),
    queryFn: () => productAPI.getProducts(page, perPage, search, category, manufacturer, includeInactive, inactiveOnly, productType),
  })
}

// Fetch single product
export function useProduct(productId: number | null) {
  return useQuery({
    queryKey: productKeys.detail(productId!),
    queryFn: () => productAPI.getProduct(productId!),
    enabled: productId !== null,
  })
}

// Fetch related products for a product (public, no auth)
export function useRelatedProducts(productId: number | null) {
  return useQuery({
    queryKey: productKeys.related(productId!),
    queryFn: () => productAPI.getRelatedProducts(productId!),
    enabled: productId !== null,
    staleTime: 5 * 60 * 1000,
  })
}

// Create product mutation
export function useCreateProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (productData: CatalogProductFormData) => productAPI.createProduct(productData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.lists() })
      queryClient.invalidateQueries({ queryKey: optionKeys.all })
      queryClient.invalidateQueries({ queryKey: categoryKeys.all })
    },
  })
}

// Update product mutation
export function useUpdateProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ productId, productData }: { productId: number; productData: Partial<CatalogProductFormData & { is_active: boolean }> }) =>
      productAPI.updateProduct(productId, productData),
    onSuccess: (data) => {
      queryClient.setQueryData(productKeys.detail(data.product.id), { product: data.product })
      queryClient.invalidateQueries({ queryKey: productKeys.lists() })
      queryClient.invalidateQueries({ queryKey: optionKeys.all })
      queryClient.invalidateQueries({ queryKey: categoryKeys.all })
    },
  })
}

// Delete product mutation
export function useDeleteProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (productId: number) => productAPI.deleteProduct(productId),
    onSuccess: (_data, productId) => {
      // Remove from detail cache
      queryClient.removeQueries({ queryKey: productKeys.detail(productId) })
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: productKeys.lists() })
    },
  })
}

// Bulk create products mutation (for CSV import)
interface BulkCreateResult {
  message: string
  created: number
  errors: Array<{ row: number; error: string }>
}

export function useBulkCreateProducts() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (products: CatalogProductFormData[]): Promise<BulkCreateResult> => {
      // This will call the bulk endpoint we'll create in Phase 6
      const response = await fetch('/api/admin/products/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ products }),
      })
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Bulk create failed')
      }
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.lists() })
    },
  })
}

// Bulk update products mutation (for bulk activate/deactivate)
interface BulkUpdateData {
  ids: number[]
  updates: Partial<{ is_active: boolean }>
}

interface BulkUpdateResult {
  message: string
  updated: number
  errors: Array<{ id: number; error: string }>
}

export function useBulkUpdateProducts() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: BulkUpdateData): Promise<BulkUpdateResult> => {
      const response = await fetch('/api/admin/products/bulk', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      })
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Bulk update failed')
      }
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all })
    },
  })
}

// Bulk delete products mutation
interface BulkDeleteResult {
  message: string
  deleted: number
  errors: Array<{ id: number; error: string }>
}

export function useBulkDeleteProducts() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (ids: number[]): Promise<BulkDeleteResult> => {
      const response = await fetch('/api/admin/products/bulk', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids }),
      })
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Bulk delete failed')
      }
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all })
    },
  })
}
